/**
 * Canary Temp Directory Helpers
 *
 * Centralizes all temporary file/directory creation under a single
 * `/tmp/canary/` base directory. This ensures the cleanup daemon only
 * removes files we own, not OS or third-party temp entries.
 *
 * All public functions lazily ensure the base directory exists on first
 * use, so callers never need to worry about directory creation.
 *
 * @module tmp
 * @see packages/tmp/README.md
 */

import os from 'node:os';
import path from 'node:path';
import fs from 'node:fs';
import { mkdir, mkdtemp } from 'node:fs/promises';
import { randomUUID } from 'node:crypto';

/** Base directory for all canary temp files */
const CANARY_TMP_DIR = path.join(os.tmpdir(), 'canary');

/** Restrictive permissions: owner-only access (rwx------) */
const DIR_MODE = 0o700;

/**
 * Tracks whether the base directory has been created this process.
 * Once created, skip redundant mkdir calls.
 */
let dirEnsured = false;

const ensureSync = (): void => {
  if (dirEnsured) return;
  fs.mkdirSync(CANARY_TMP_DIR, { recursive: true, mode: DIR_MODE });
  dirEnsured = true;
};

const ensureAsync = async (): Promise<void> => {
  if (dirEnsured) return;
  await mkdir(CANARY_TMP_DIR, { recursive: true, mode: DIR_MODE });
  dirEnsured = true;
};

/** Get the canary tmp base directory path (lazily ensures dir exists) */
export const getCanaryTmpDir = (): string => {
  ensureSync();
  return CANARY_TMP_DIR;
};

/** Ensure the canary base directory exists (async, idempotent) */
export const ensureCanaryTmpDir = async (): Promise<string> => {
  await ensureAsync();
  return CANARY_TMP_DIR;
};

/**
 * Get a named subdirectory under the canary tmp dir, creating it if needed.
 *
 * Accepts nested paths (e.g. `'workflow-runs/abc-123'`).
 *
 * @example
 * const dir = getSubDir('cache-agent');
 * // => '/tmp/canary/cache-agent' (created with 0o700 permissions)
 */
export const getSubDir = (name: string): string => {
  ensureSync();
  const dir = path.join(CANARY_TMP_DIR, name);
  fs.mkdirSync(dir, { recursive: true, mode: DIR_MODE });
  return dir;
};

/**
 * Async version of getSubDir. Prefer this in async contexts to avoid
 * blocking the event loop on first call.
 */
export const getSubDirAsync = async (name: string): Promise<string> => {
  await ensureAsync();
  const dir = path.join(CANARY_TMP_DIR, name);
  await mkdir(dir, { recursive: true, mode: DIR_MODE });
  return dir;
};

/** Create a temp directory under /tmp/canary/ with the given prefix */
export const makeTempDir = async (prefix: string): Promise<string> => {
  await ensureAsync();
  return mkdtemp(path.join(CANARY_TMP_DIR, prefix));
};

/** Synchronous version of makeTempDir */
export const makeTempDirSync = (prefix: string): string => {
  ensureSync();
  return fs.mkdtempSync(path.join(CANARY_TMP_DIR, prefix));
};

/** Generate a temp file path under /tmp/canary/ (ensures dir exists, does NOT create the file) */
export const getTempFilePath = (prefix: string, suffix: string = ''): string => {
  ensureSync();
  return path.join(CANARY_TMP_DIR, `${prefix}${randomUUID()}${suffix}`);
};

// ============================================================================
// Org-scoped helpers — isolate temp files by organization ID
// ============================================================================

/** UUID v4 pattern — all orgIds in this codebase are UUIDs */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Validate orgId is a UUID to prevent path traversal */
const validateOrgId = (orgId: string): void => {
  if (!UUID_RE.test(orgId)) {
    throw new Error(`Invalid orgId for tmp path: expected UUID, got "${orgId}"`);
  }
};

/** Get the org-scoped tmp base directory: `/tmp/canary/{orgId}/` */
export const getOrgTmpDir = (orgId: string): string => {
  validateOrgId(orgId);
  ensureSync();
  const dir = path.join(CANARY_TMP_DIR, orgId);
  fs.mkdirSync(dir, { recursive: true, mode: DIR_MODE });
  return dir;
};

/** Get a named subdirectory under the org-scoped tmp dir, creating it synchronously. */
export const getOrgSubDir = (opts: { orgId: string; name: string }): string => {
  validateOrgId(opts.orgId);
  ensureSync();
  const dir = path.join(CANARY_TMP_DIR, opts.orgId, opts.name);
  fs.mkdirSync(dir, { recursive: true, mode: DIR_MODE });
  return dir;
};

/** Async version of getOrgSubDir. */
export const getOrgSubDirAsync = async (opts: { orgId: string; name: string }): Promise<string> => {
  validateOrgId(opts.orgId);
  await ensureAsync();
  const dir = path.join(CANARY_TMP_DIR, opts.orgId, opts.name);
  await mkdir(dir, { recursive: true, mode: DIR_MODE });
  return dir;
};

/** Create a temp directory under `/tmp/canary/{orgId}/` with the given prefix. */
export const makeOrgTempDir = async (opts: { orgId: string; prefix: string }): Promise<string> => {
  const base = getOrgTmpDir(opts.orgId);
  return mkdtemp(path.join(base, opts.prefix));
};

/** Synchronous version of makeOrgTempDir. */
export const makeOrgTempDirSync = (opts: { orgId: string; prefix: string }): string => {
  const base = getOrgTmpDir(opts.orgId);
  return fs.mkdtempSync(path.join(base, opts.prefix));
};

/** Generate a temp file path under `/tmp/canary/{orgId}/` (ensures dir exists, does NOT create the file). */
export const getOrgTempFilePath = (opts: { orgId: string; prefix: string; suffix?: string }): string => {
  const base = getOrgTmpDir(opts.orgId);
  return path.join(base, `${opts.prefix}${randomUUID()}${opts.suffix ?? ''}`);
};
